import React, { useState, useCallback, lazy, Suspense } from "react"
import size from "lodash/size"
import trim from "lodash/trim"
import head from "lodash/head"
import isString from "lodash/isString"
import { I18n, Translate } from "utils/i18n-shim"
import InputLabel from "@mui/material/InputLabel"
import Box from "@mui/material/Box"
import { orange } from "components/App/theme"
import { ErrorBoundary } from "react-error-boundary"
import CodeEditor from "components/TouchDesign/Components/CodeEditor"
import { Prism } from "shared/lib/prism"
import makeStyles from "@mui/styles/makeStyles"
const SummernoteEditor = lazy(() => import("shared/ui/SummernoteEditor"))

const useLabelStyles = makeStyles({
  asterisk: { color: "red" },
  root: {
    fontWeight: "bold",
    color: "black",
    fontSize: 13,
    transform: "translate(5px, 0) !important"
  }
})

const useStyles = makeStyles({
  error: {
    position: "absolute",
    bottom: -15,
    fontSize: 12,
    lineHeight: "12px"
  },
  parseFailureError: {
    color: "red"
  },
  editor: {
    boxShadow: "0px 0px 5px 1px rgb(225, 225, 225)",
    border: "1px solid #eee",
    borderRadius: 4,
    "&:hover": {
      borderColor: orange
    },
    "& .note-editor.note-frame": {
      border: "none"
    }
  },
  focusedEditor: {
    borderColor: orange,
    borderWidth: 2,
    margin: -1
  }
})

const Wysiwyg = ({
  id,
  placeholder = "",
  disabled = false,
  required = false,
  label,
  error = {},
  errorLabels = { errorKey: "" },
  dirty = false,
  onChange,
  onFocus = null,
  onBlur = null,
  value
}) => {
  const [focused, setFocused] = useState(false)
  const labelClasses = useLabelStyles()
  const classes = useStyles()
  const onHighlight = useCallback((code) => {
    return Prism.highlight(code ?? "", Prism.languages.html, "markup")
  }, [])

  const errors = Object.keys(error)
  /* eslint-disable no-nested-ternary */
  const errorKey =
    dirty && size(errors) ? (isString(error) ? error : head(errors)) : ""
  /* eslint-enable no-nested-ternary */
  const errorText =
    !errorKey ||
    (errorKey === "required" && size(errors) === 1 && trim(value)
      ? ""
      : errorLabels?.errorKey ?? I18n.t(`errors.${errorKey}`))

  const onFocusHandler = useCallback(() => {
    setFocused(true)
    onFocus?.()
  }, [onFocus])

  const onBlurHandler = useCallback(() => {
    setFocused(false)
    onBlur?.()
  }, [onBlur])

  return (
    <div id={id}>
      <Box marginBottom="10px">
        <InputLabel classes={labelClasses} required={required}>
          {label}
        </InputLabel>
      </Box>

      <ErrorBoundary
        fallbackRender={() => {
          return (
            <div>
              <CodeEditor
                value={value}
                name={name}
                onChange={onChange}
                highlight={onHighlight}
              />
              <div className={classes.parseFailureError}>
                <Translate value="errors.invalid-wysiwyg-content" />
              </div>
            </div>
          )
        }}
      >
        <div
          className={`${classes.editor} ${focused ? classes.focusedEditor : ""}`}
        >
          <Suspense fallback={null}>
            <SummernoteEditor
              value={value}
              onChange={onChange}
              onFocus={onFocusHandler}
              onBlur={onBlurHandler}
              placeholder={placeholder}
              disabled={disabled}
            />
          </Suspense>
        </div>
      </ErrorBoundary>
      <div className={classes.error}>{errorText}</div>
    </div>
  )
}

export default Wysiwyg
